// recipeStandardization.js - Central Culinary Standards, Autocomplete Dictionary, and Parsers

export const STANDARD_CUISINES = [
  "American",
  "Italian",
  "Mexican",
  "Asian",
  "Mediterranean",
  "French",
  "BBQ & Grilling",
  "Breakfast & Brunch",
  "Dessert & Baking",
  "Seafood",
  "Soups & Stews",
  "Indian",
  "Thai",
  "Japanese",
  "Chinese",
  "Greek",
  "Latin American",
  "Middle Eastern",
  "Southern & Comfort",
  "Vegetarian & Vegan",
  "Healthy & Light",
  "Drinks & Cocktails",
  "European",
  "General"
];

export const STANDARD_MEASUREMENTS = [
  "tsp", "tbsp", "cup", "oz", "fl oz", "lb", "gram", "kg", "ml", "l",
  "pint", "quart", "gallon", "pinch", "dash", "clove", "slice",
  "can", "jar", "package", "box", "stalk", "bunch", "head", "piece", "whole"
];

export const UNIT_MAPPINGS = {
  "tablespoon": "tbsp", "tablespoons": "tbsp", "tbsp": "tbsp", "tbs": "tbsp", "tb": "tbsp", "T": "tbsp",
  "teaspoon": "tsp", "teaspoons": "tsp", "tsp": "tsp", "ts": "tsp", "t": "tsp",
  "cup": "cup", "cups": "cup", "c": "cup",
  "ounce": "oz", "ounces": "oz", "oz": "oz",
  "fluid ounce": "fl oz", "fluid ounces": "fl oz", "fl oz": "fl oz", "floz": "fl oz",
  "pound": "lb", "pounds": "lb", "lb": "lb", "lbs": "lb",
  "gram": "gram", "grams": "gram", "g": "gram",
  "kilogram": "kg", "kilograms": "kg", "kg": "kg", "kilo": "kg",
  "liter": "l", "liters": "l", "l": "l",
  "milliliter": "ml", "milliliters": "ml", "ml": "ml",
  "quart": "quart", "quarts": "quart", "qt": "quart",
  "pint": "pint", "pints": "pint", "pt": "pint",
  "gallon": "gallon", "gallons": "gallon", "gal": "gallon",
  "pinch": "pinch", "pinches": "pinch",
  "dash": "dash", "dashes": "dash",
  "clove": "clove", "cloves": "clove",
  "slice": "slice", "slices": "slice",
  "can": "can", "cans": "can",
  "jar": "jar", "jars": "jar",
  "package": "package", "packages": "package", "pkg": "package", "pkgs": "package",
  "box": "box", "boxes": "box",
  "stalk": "stalk", "stalks": "stalk",
  "bunch": "bunch", "bunches": "bunch",
  "head": "head", "heads": "head",
  "piece": "piece", "pieces": "piece", "pc": "piece",
  "whole": "whole"
};

// Seed Master Culinary Ingredients (Alphabetized & Comprehensive)
export const SEED_INGREDIENTS = [
  "All-Purpose Flour",
  "Almond Milk",
  "Apple",
  "Apple Cider Vinegar",
  "Avocado",
  "Avocado Oil",
  "Bacon",
  "Baking Powder",
  "Baking Soda",
  "Balsamic Vinegar",
  "Banana",
  "Basil",
  "Bay Leaves",
  "Beef Broth",
  "Bell Pepper",
  "Black Beans",
  "Black Pepper",
  "Bread Crumbs",
  "Broccoli",
  "Brown Rice",
  "Brown Sugar",
  "Butter",
  "Cabbage",
  "Carrot",
  "Cayenne Pepper",
  "Celery",
  "Cheddar Cheese",
  "Chicken Breast",
  "Chicken Broth",
  "Chicken Thighs",
  "Chili Powder",
  "Chives",
  "Cilantro",
  "Cinnamon",
  "Cocoa Powder",
  "Coconut Milk",
  "Coconut Oil",
  "Cornstarch",
  "Cream Cheese",
  "Cream of Tartar",
  "Cucumber",
  "Cumin",
  "Dijon Mustard",
  "Dill",
  "Eggs",
  "Evaporated Milk",
  "Extra Virgin Olive Oil",
  "Feta Cheese",
  "Garlic",
  "Garlic Powder",
  "Ginger",
  "Greek Yogurt",
  "Green Onions",
  "Ground Beef",
  "Ground Cinnamon",
  "Heavy Cream",
  "Honey",
  "Jalapeño",
  "Kale",
  "Kosher Salt",
  "Lemon Juice",
  "Lemon Zest",
  "Lime Juice",
  "Maple Syrup",
  "Mayonnaise",
  "Milk",
  "Mozzarella Cheese",
  "Mushrooms",
  "Nutmeg",
  "Oats",
  "Olive Oil",
  "Onion",
  "Onion Powder",
  "Oregano",
  "Paprika",
  "Parmesan Cheese",
  "Parsley",
  "Peanut Butter",
  "Pecans",
  "Potatoes",
  "Powdered Sugar",
  "Quinoa",
  "Red Onion",
  "Red Pepper Flakes",
  "Rosemary",
  "Salt",
  "Scallions",
  "Sesame Oil",
  "Sesame Seeds",
  "Shallots",
  "Sour Cream",
  "Soy Sauce",
  "Spinach",
  "Sugar",
  "Sweet Potatoes",
  "Thyme",
  "Tomatoes",
  "Tomato Paste",
  "Tomato Sauce",
  "Vanilla Extract",
  "Vegetable Broth",
  "Vegetable Oil",
  "Walnuts",
  "White Onion",
  "White Sugar",
  "Worcestershire Sauce",
  "Yeast",
  "Yellow Onion",
  "Zucchini"
];

// Capitalize words cleanly
export const toTitleCase = (str) => {
  if (!str) return "";
  return str
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map(word => {
      // Keep small prepositions lowercase if not the first word
      const smallWords = ["of", "and", "in", "with", "on", "the", "a", "an"];
      if (smallWords.includes(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ")
    // Ensure first letter is always capitalized
    .replace(/^./, c => c.toUpperCase());
};

// String distance for fuzzy matching
const levenshtein = (a, b) => {
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1));
      }
    }
  }
  return matrix[b.length][a.length];
};

// Parse mixed fractions like "1 1/2" or "3/4" to decimals/clean strings
export const parseFraction = (value) => {
  if (!value) return "";
  if (typeof value === "number") return value;
  const strVal = String(value).trim();
  try {
    const parts = strVal.replace('-', ' ').split(' ');
    let total = 0;
    for (const part of parts) {
      if (part.includes('/')) {
        const [num, den] = part.split('/');
        if (parseFloat(den) !== 0) total += parseFloat(num) / parseFloat(den);
      } else if (!isNaN(part) && part !== "") {
        total += parseFloat(part);
      }
    }
    return total > 0 ? parseFloat(total.toFixed(3)) : strVal;
  } catch (e) {
    return strVal;
  }
};

// Match input unit to standardized unit
export const getStandardizedUnit = (inputUnit) => {
  if (!inputUnit) return "";
  const normalized = inputUnit.toLowerCase().trim().replace(/[.,s]$/g, "");
  if (UNIT_MAPPINGS[normalized]) return UNIT_MAPPINGS[normalized];
  if (UNIT_MAPPINGS[inputUnit.toLowerCase().trim()]) return UNIT_MAPPINGS[inputUnit.toLowerCase().trim()];

  const keys = Object.keys(UNIT_MAPPINGS);
  let bestMatch = null;
  let lowestDist = Infinity;
  for (const key of keys) {
    const dist = levenshtein(normalized, key);
    if (dist < lowestDist && dist <= 2 && key.length > 2) {
      lowestDist = dist;
      bestMatch = key;
    }
  }
  return bestMatch ? UNIT_MAPPINGS[bestMatch] : inputUnit;
};

// Parse a single raw ingredient string into { amount, measurement_name, ingredient_name }
export const parseIngredientLine = (line) => {
  let str = line.trim();
  // Strip leading bullets, dashes, asterisks, numbers with periods (e.g. "1. ", "- ")
  str = str.replace(/^[-*•\d+.]\s*/, "").trim();

  let amount = "";
  let unit = "";
  let name = str;

  // Amount regex matches: "1 1/2", "1-1/2", "1/2", "0.5", "2", "3.75"
  const amountRegex = /^(\d+[\s-]+\d+\/\d+|\d+\/\d+|\d+(\.\d+)?)/;
  const match = str.match(amountRegex);

  if (match) {
    amount = match[0].replace(/-/g, " ").trim();
    str = str.substring(match[0].length).trim();

    // Check first word or two words for unit (e.g. "fluid ounce", "fl oz", "tbsp", "cup")
    const words = str.split(/\s+/);
    if (words.length >= 2) {
      const twoWords = `${words[0]} ${words[1]}`.toLowerCase().replace(/[.,]$/g, "");
      if (UNIT_MAPPINGS[twoWords]) {
        unit = UNIT_MAPPINGS[twoWords];
        name = words.slice(2).join(" ").trim();
        return { amount: parseFraction(amount), measurement_name: unit, ingredient_name: toTitleCase(name) };
      }
    }

    if (words.length >= 1) {
      const firstWord = words[0].toLowerCase().replace(/[.,]$/g, "");
      const cleanUnit = getStandardizedUnit(firstWord);
      if (UNIT_MAPPINGS[firstWord] || STANDARD_MEASUREMENTS.includes(cleanUnit)) {
        unit = cleanUnit;
        name = words.slice(1).join(" ").trim();
        return { amount: parseFraction(amount), measurement_name: unit, ingredient_name: toTitleCase(name) };
      }
    }
  }

  return {
    amount: amount ? parseFraction(amount) : "",
    measurement_name: unit,
    ingredient_name: toTitleCase(name)
  };
};

// Parse a multiline text block into array of ingredient objects
export const parseBulkIngredients = (text) => {
  if (!text) return [];
  const lines = text
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(l => l.length > 0);

  return lines.map(line => parseIngredientLine(line));
};

// Detect duplicate ingredients in a recipe list
export const findDuplicateIngredients = (ingredients) => {
  const seen = {};
  const duplicates = new Set();

  ingredients.forEach(ing => {
    const name = (ing.ingredient_name || "").trim().toLowerCase();
    if (!name) return;
    if (seen[name]) {
      duplicates.add(toTitleCase(name));
    } else {
      seen[name] = true;
    }
  });

  return Array.from(duplicates);
};

// Format a numeric value or decimal into clean culinary fractions
export const formatFraction = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = typeof value === "number" ? value : parseFloat(value);
  if (isNaN(num)) return String(value);
  if (num <= 0) return "0";

  const whole = Math.floor(num);
  const remainder = num - whole;

  const fractions = [
    { value: 0, str: "" },
    { value: 0.125, str: "1/8" },
    { value: 0.25, str: "1/4" },
    { value: 0.333, str: "1/3" },
    { value: 0.375, str: "3/8" },
    { value: 0.5, str: "1/2" },
    { value: 0.625, str: "5/8" },
    { value: 0.667, str: "2/3" },
    { value: 0.75, str: "3/4" },
    { value: 0.875, str: "7/8" },
    { value: 1, str: "1" }
  ];

  let closest = fractions[0];
  let minDiff = Math.abs(remainder - fractions[0].value);
  for (let i = 1; i < fractions.length; i++) {
    const diff = Math.abs(remainder - fractions[i].value);
    if (diff < minDiff) {
      minDiff = diff;
      closest = fractions[i];
    }
  }

  // If closest difference is within culinary tolerance (~0.045)
  if (minDiff <= 0.045) {
    if (closest.value === 1) {
      return String(whole + 1);
    }
    if (closest.value === 0) {
      return whole > 0 ? String(whole) : "0";
    }
    return whole > 0 ? `${whole} ${closest.str}` : closest.str;
  }

  // Otherwise, round to at most 2 decimal places
  const rounded = Math.round(num * 100) / 100;
  return String(rounded);
};

// Scale an ingredient's amount according to a multiplier (e.g. 1.5x, 2x, 0.5x)
export const scaleIngredientAmount = (amount, multiplier) => {
  if (amount === null || amount === undefined || amount === "") return "";
  if (!multiplier || multiplier === 1) {
    const parsed = parseFraction(amount);
    return typeof parsed === "number" ? formatFraction(parsed) : String(amount);
  }
  const parsed = parseFraction(amount);
  if (typeof parsed !== "number" || isNaN(parsed)) {
    return String(amount);
  }
  const scaled = parsed * multiplier;
  return formatFraction(scaled);
};

// Scan instruction text for duration mentions (e.g., "15 minutes", "1 hour", "30-35 mins", "45 seconds")
export const detectTimers = (text) => {
  if (!text) return [];
  const timers = [];
  const regex = /(\d+(?:\.\d+)?|\d+\s*-\s*\d+)\s*(hours?|hrs?|minutes?|mins?|seconds?|secs?)/gi;
  let match;
  while ((match = regex.exec(text)) !== null) {
    const rawNum = match[1];
    const unit = match[2].toLowerCase();
    let num = 0;
    if (rawNum.includes('-')) {
      const parts = rawNum.split('-').map(p => parseFloat(p.trim()));
      num = parts[1] || parts[0];
    } else {
      num = parseFloat(rawNum);
    }
    if (isNaN(num) || num <= 0) continue;

    let totalSeconds = 0;
    if (unit.startsWith('h')) {
      totalSeconds = Math.round(num * 3600);
    } else if (unit.startsWith('m')) {
      totalSeconds = Math.round(num * 60);
    } else if (unit.startsWith('s')) {
      totalSeconds = Math.round(num);
    }

    timers.push({
      label: match[0].trim(),
      seconds: totalSeconds
    });
  }
  return timers;
};
